/**
 * Custom Edge Component - Modular & Feature-Rich
 * Supports: line styles, colors, widths, arrow types, arrow sizes, animation, easy selection
 * Each edge has independent properties stored in edge.data
 */

import { memo, useRef, useState, useEffect, useCallback } from 'react'
import { EdgeProps, getStraightPath, getBezierPath, getSmoothStepPath, EdgeLabelRenderer, useReactFlow, useInternalNode } from '@xyflow/react'
import ReactMarkdown from 'react-markdown'
import { generateArrowMarker, getArrowMarkerRefs, type ArrowType } from '../../utils/edge-markers'
import { getFloatingEdgeParams, getPerimeterPoint, getNodeRotation, rotateHandleOffset } from '../../utils/floatingEdge'

// ============================================================================
// CONSTANTS & TYPES
// ============================================================================

const LINE_STYLES = {
  solid: undefined,
  dashed: '8 8',
  dotted: '2 4',
  longdash: '16 8',
} as const

interface EdgeData {
  lineStyle?: keyof typeof LINE_STYLES
  lineWidth?: number
  lineCap?: 'butt' | 'round' | 'square'
  lineJoin?: 'miter' | 'round' | 'bevel'
  color?: string
  opacity?: number
  arrowStart?: ArrowType
  arrowEnd?: ArrowType
  arrowSize?: number
  bidirectional?: boolean
  protocol?: string
  labelFontSize?: number
  labelBackground?: string
  labelBackgroundColor?: string
  labelZIndex?: number
  labelPosition?: number  // 0.0 to 1.0, position along path (default 0.5 = middle)
  labelMode?: 'default' | 'split' | 'markdown'  // Label rendering mode
  labelText?: string  // For split mode (bottom text) or markdown content
  shadowEnabled?: boolean
  shadowColor?: string
  shadowBlur?: number
  shadowOffsetX?: number
  shadowOffsetY?: number
  zIndex?: number
  offset?: number  // Path offset for parallel/bidirectional edges (pixels)
  edgeDirection?: 'forward' | 'backward' | 'single'  // Direction in bidirectional pair
  pathStyle?: 'straight' | 'bezier' | 'smoothstep'  // Connector routing shape (Properties > Routing)
  targetAngle?: number  // Arrowhead attach angle in degrees around the target node (Properties > Arrows)
  fixedConnection?: boolean  // Pin to the assigned 12-handle instead of floating boundary attachment
}

// ============================================================================
// HELPER FUNCTIONS FOR LABEL POSITIONING
// ============================================================================

/**
 * Get coordinates at a percentage along the SVG path
 * @param pathElement SVG path element
 * @param percentage 0.0 to 1.0 (0 = start, 1 = end)
 * @returns {x, y} coordinates
 */
function getPointAlongPath(
  pathElement: SVGPathElement | null,
  percentage: number
): { x: number; y: number } {
  if (!pathElement) return { x: 0, y: 0 }

  try {
    const totalLength = pathElement.getTotalLength()
    const distance = totalLength * Math.max(0, Math.min(1, percentage))
    const point = pathElement.getPointAtLength(distance)
    return { x: point.x, y: point.y }
  } catch (e) {
    return { x: 0, y: 0 }
  }
}

/**
 * Find closest point on path to mouse position
 * Returns percentage (0.0 to 1.0) along path
 */
function findClosestPointOnPath(
  pathElement: SVGPathElement,
  mouseX: number,
  mouseY: number
): number {
  const totalLength = pathElement.getTotalLength()
  const samples = 100 // Sample at 100 points for accuracy

  let closestPercentage = 0.5
  let minDistance = Infinity

  for (let i = 0; i <= samples; i++) {
    const percentage = i / samples
    const distance = totalLength * percentage
    const point = pathElement.getPointAtLength(distance)

    const dx = point.x - mouseX
    const dy = point.y - mouseY
    const dist = Math.sqrt(dx * dx + dy * dy)

    if (dist < minDistance) {
      minDistance = dist
      closestPercentage = percentage
    }
  }

  return closestPercentage
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function CustomEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  sourceHandleId,
  targetHandleId,
  style = {},
  label,
  data,
  animated,
  selected,
}: EdgeProps) {
  const { setEdges } = useReactFlow()
  const pathRef = useRef<SVGPathElement>(null)
  const [isDraggingLabel, setIsDraggingLabel] = useState(false)

  // Get edge properties from data (with proper type casting)
  const edgeData = data as EdgeData | undefined
  const lineStyle = (edgeData?.lineStyle as keyof typeof LINE_STYLES) || 'solid'
  const baseLineWidth = (edgeData?.lineWidth as number) || 2
  const lineCap = (edgeData?.lineCap || 'butt') as 'butt' | 'round' | 'square'
  const lineJoin = (edgeData?.lineJoin || 'miter') as 'miter' | 'round' | 'bevel'
  const baseColor = (edgeData?.color as string) || '#b1b1b7'
  const opacity = (edgeData?.opacity as number) !== undefined ? (edgeData?.opacity as number) : 1

  // FIX ISSUE 1: Enhanced visual prominence for selection
  // Since SVG z-index doesn't work, make selected edges MUCH more visible
  const lineWidth = selected ? baseLineWidth + 4 : baseLineWidth  // Increased from +2 to +4
  const color = selected ? '#3b82f6' : baseColor
  const arrowStart = (edgeData?.arrowStart as ArrowType) || 'none'
  const arrowEnd = (edgeData?.arrowEnd as ArrowType) || 'arrow'
  const arrowSize = (edgeData?.arrowSize as number) || 1
  const bidirectional = edgeData?.bidirectional === true
  const protocol = edgeData?.protocol as string | undefined
  const isAnimated = animated === true
  const labelFontSize = (edgeData?.labelFontSize as number) || 12
  const labelBackground = (edgeData?.labelBackground as string) || 'white'
  const labelBackgroundColor = (edgeData?.labelBackgroundColor as string) || '#ffffff'
  const labelZIndex = (edgeData?.labelZIndex as number) || 1
  const shadowEnabled = edgeData?.shadowEnabled === true
  const shadowColor = (edgeData?.shadowColor as string) || '#000000'
  const shadowBlur = (edgeData?.shadowBlur as number) || 3
  const shadowOffsetX = (edgeData?.shadowOffsetX as number) || 2
  const shadowOffsetY = (edgeData?.shadowOffsetY as number) || 2
  // FIX ISSUE 1: Set higher base z-index and boost for selection
  // Note: This only affects HTML-rendered edge labels, not SVG paths
  const baseZIndex = (edgeData?.zIndex as number) ?? 50  // Increased from 0 to 50
  const zIndex = selected ? baseZIndex + 100 : baseZIndex  // Selected edges get +100
  const labelPosition = (edgeData?.labelPosition as number) ?? 0.5
  const labelMode = (edgeData?.labelMode as 'default' | 'split' | 'markdown') || 'default'
  const labelText = (edgeData?.labelText as string) || ''
  // Per-edge override for where the arrowhead attaches on the target node,
  // measured in degrees around the node center (undefined = auto/floating).
  const targetAngle = (edgeData?.targetAngle as number | undefined)
  // When the user pins this edge to fixed connection points, we SKIP the
  // floating boundary attachment and use React Flow's handle positions
  // (sourceX/Y/sourcePosition/targetX/Y/targetPosition) directly so the edge
  // stays attached to its assigned 12-handle.
  const fixedConnection = edgeData?.fixedConnection === true

  // Floating attachment: connect to the boundary point of each node that faces
  // the other node, so the arrowhead enters PERPENDICULAR to that side and
  // always points cleanly into the node (no more sideways-bent arrows). Falls
  // back to React Flow's handle positions until both nodes are measured.
  const sourceNode = useInternalNode(source)
  const targetNode = useInternalNode(target)
  const floating =
    !fixedConnection &&
    sourceNode?.measured?.width != null &&
    targetNode?.measured?.width != null
      ? getFloatingEdgeParams(sourceNode, targetNode)
      : null
  // A manual arrowhead angle (Properties > Arrows) overrides the auto floating
  // attachment on the target node — spin the arrow anywhere around the node.
  const angled =
    targetAngle != null && targetNode?.measured?.width != null
      ? getPerimeterPoint(targetNode, targetAngle)
      : null
  const sX = floating ? floating.sx : sourceX
  const sY = floating ? floating.sy : sourceY
  const tX = angled ? angled.x : floating ? floating.tx : targetX
  const tY = angled ? angled.y : floating ? floating.ty : targetY
  const sPos = floating ? floating.sourcePos : sourcePosition
  const tPos = angled ? angled.pos : floating ? floating.targetPos : targetPosition

  // SEAM-6: when this edge is pinned to a fixed 12-handle (fixedConnection) on a
  // ROTATED node, React Flow still reports the handle's UN-rotated DOM position
  // (handles stay un-rotated below the rotated visual). Move the endpoint to
  // where that handle physically sits after rotation so the pinned arrow meets
  // the rotated shape's visible side. No-op when rotation is 0 or the id is not
  // a 12-handle (then we keep React Flow's reported position).
  let fsX = sX, fsY = sY, ftX = tX, ftY = tY
  if (fixedConnection && sourceNode?.measured?.width != null) {
    const r = getNodeRotation(sourceNode)
    if (r !== 0 && typeof sourceHandleId === 'string') {
      const w = sourceNode.measured.width
      const h = sourceNode.measured.height ?? w
      const cx = sourceNode.internals.positionAbsolute.x + w / 2
      const cy = sourceNode.internals.positionAbsolute.y + h / 2
      const p = rotateHandleOffset(cx, cy, w / 2, h / 2, sourceHandleId, r)
      if (p) { fsX = p.x; fsY = p.y }
    }
  }
  if (fixedConnection && targetNode?.measured?.width != null) {
    const r = getNodeRotation(targetNode)
    if (r !== 0 && typeof targetHandleId === 'string') {
      const w = targetNode.measured.width
      const h = targetNode.measured.height ?? w
      const cx = targetNode.internals.positionAbsolute.x + w / 2
      const cy = targetNode.internals.positionAbsolute.y + h / 2
      const p = rotateHandleOffset(cx, cy, w / 2, h / 2, targetHandleId, r)
      if (p) { ftX = p.x; ftY = p.y }
    }
  }

  // Connector routing is a per-edge property (Properties > Routing):
  //  - 'straight'   : direct line, arrowhead points dead-on into the node
  //  - 'bezier'     : smooth curve
  //  - 'smoothstep' : orthogonal elbows (honors the parallel-separation offset)
  const pathStyle = (edgeData?.pathStyle as 'straight' | 'bezier' | 'smoothstep') || 'smoothstep'
  const [edgePath, labelX, labelY] =
    pathStyle === 'bezier'
      ? getBezierPath({ sourceX: fsX, sourceY: fsY, sourcePosition: sPos, targetX: ftX, targetY: ftY, targetPosition: tPos })
      : pathStyle === 'smoothstep'
        ? getSmoothStepPath({ sourceX: fsX, sourceY: fsY, sourcePosition: sPos, targetX: ftX, targetY: ftY, targetPosition: tPos })
        : getStraightPath({ sourceX: fsX, sourceY: fsY, targetX: ftX, targetY: ftY })

  // Calculate label position along path (if path element available)
  const calculatedPosition = getPointAlongPath(pathRef.current, labelPosition)
  const finalLabelX = pathRef.current ? calculatedPosition.x : labelX
  const finalLabelY = pathRef.current ? calculatedPosition.y : labelY

  // Drag handlers for label
  const handleLabelMouseDown = useCallback((e: React.MouseEvent) => {
    if (!selected) return
    e.stopPropagation()
    setIsDraggingLabel(true)
  }, [selected])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingLabel || !pathRef.current) return

    // Get SVG element
    const svg = pathRef.current.ownerSVGElement
    if (!svg) return

    // Convert mouse position to SVG coordinates
    const pt = svg.createSVGPoint()
    pt.x = e.clientX
    pt.y = e.clientY
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse())

    // Find closest point on path
    const newPercentage = findClosestPointOnPath(pathRef.current, svgP.x, svgP.y)

    // Update edge data
    setEdges((edges) =>
      edges.map((edge) =>
        edge.id === id
          ? {
              ...edge,
              data: {
                ...edge.data,
                labelPosition: newPercentage,
              },
            }
          : edge
      )
    )
  }, [isDraggingLabel, id, setEdges])

  const handleMouseUp = useCallback(() => {
    setIsDraggingLabel(false)
  }, [])

  // Add/remove mouse event listeners for dragging
  useEffect(() => {
    if (isDraggingLabel) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDraggingLabel, handleMouseMove, handleMouseUp])

  // Determine which markers to use (shared logic with SmartEdge)
  const { markerStart, markerEnd } = getArrowMarkerRefs(id, arrowStart, arrowEnd, bidirectional)

  return (
    <>
      {/* SVG Marker Definitions - unique per edge */}
      <defs>
        {/* Start arrow markers */}
        {(bidirectional || arrowStart !== 'none') && generateArrowMarker(
          id,
          arrowStart === 'none' ? 'arrow' : arrowStart,
          'start',
          color,
          opacity,
          arrowSize
        )}

        {/* End arrow markers */}
        {arrowEnd !== 'none' && generateArrowMarker(
          id,
          arrowEnd,
          'end',
          color,
          opacity,
          arrowSize
        )}
      </defs>

      {/* Invisible wider path for easier clicking */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={Math.max(20, lineWidth * 3)}
        className="react-flow__edge-interaction"
        style={{ cursor: 'pointer' }}
      />

      {/* Visible edge path */}
      <path
        ref={pathRef}
        id={id}
        style={{
          ...style,
          // Our custom properties override any conflicting style props
          stroke: color,
          strokeWidth: lineWidth,
          strokeDasharray: LINE_STYLES[lineStyle],
          strokeLinecap: lineCap,
          strokeLinejoin: lineJoin,
          opacity: opacity,
          fill: 'none',
          filter: selected
            ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.6))'
            : shadowEnabled
              ? `drop-shadow(${shadowOffsetX}px ${shadowOffsetY}px ${shadowBlur}px ${shadowColor})`
              : undefined,
          zIndex: zIndex,
          transition: 'stroke-width 0.2s ease, stroke 0.2s ease, filter 0.2s ease',
        }}
        className={`react-flow__edge-path ${isAnimated ? 'edge-animated' : ''} ${isAnimated ? `edge-style-${lineStyle}` : ''}`}
        d={edgePath}
        markerStart={markerStart}
        markerEnd={markerEnd}
      />

      {/* Label */}
      {label && (
        <EdgeLabelRenderer>
          <div
            onMouseDown={handleLabelMouseDown}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${finalLabelX}px,${finalLabelY}px)`,
              fontSize: labelFontSize,
              fontWeight: 500,
              background: labelBackground === 'none' ? 'transparent' :
                         labelBackground === 'custom' ? labelBackgroundColor :
                         'white',
              padding: '4px 8px',
              borderRadius: '4px',
              border: labelBackground === 'none' ? 'none' : selected ? '2px solid #3b82f6' : '1px solid #d0d0d0',
              pointerEvents: 'all',
              zIndex: labelZIndex,
              cursor: selected ? (isDraggingLabel ? 'grabbing' : 'grab') : 'default',
              boxShadow: selected ? '0 2px 8px rgba(59, 130, 246, 0.3)' : undefined,
              transition: isDraggingLabel ? 'none' : 'all 0.2s ease',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: '6px',
            }}
            className="nodrag nopan"
          >
            {/* Drag handle - on the left */}
            {selected && (
              <span style={{
                fontSize: '12px',
                color: '#3b82f6',
                opacity: 0.8,
                userSelect: 'none',
                paddingTop: '2px',
              }}>
                ⋮⋮
              </span>
            )}

            {/* Content wrapper */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              flex: 1,
            }}>
              {/* Render based on label mode */}
              {labelMode === 'default' && (
                <>
                  <span>{String(label)}</span>
                  {protocol && (
                    <div style={{ fontSize: labelFontSize * 0.8, color: '#666', marginTop: 2 }}>
                      <span>{protocol}</span>
                    </div>
                  )}
                </>
              )}

              {labelMode === 'split' && (
                <>
                  <div style={{
                    fontWeight: 600,
                    borderBottom: '1px solid #d1d5db',
                    paddingBottom: '4px',
                    width: '100%',
                    textAlign: 'center'
                  }}>
                    {String(label)}
                  </div>
                  {labelText && (
                    <div style={{
                      fontSize: labelFontSize * 0.9,
                      color: '#6b7280',
                      paddingTop: '4px',
                      width: '100%',
                      textAlign: 'center',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {labelText}
                    </div>
                  )}
                </>
              )}

              {labelMode === 'markdown' && labelText && (
                <div style={{
                  fontSize: labelFontSize,
                  width: '100%',
                }}>
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p style={{ margin: 0, marginBottom: '4px' }}>{children}</p>,
                      h1: ({ children }) => <h1 style={{ fontSize: labelFontSize * 1.5, margin: 0, marginBottom: '4px' }}>{children}</h1>,
                      h2: ({ children }) => <h2 style={{ fontSize: labelFontSize * 1.3, margin: 0, marginBottom: '4px' }}>{children}</h2>,
                      h3: ({ children }) => <h3 style={{ fontSize: labelFontSize * 1.1, margin: 0, marginBottom: '4px' }}>{children}</h3>,
                      hr: () => <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #d1d5db' }} />,
                      strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
                      em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                      ul: ({ children }) => <ul style={{ margin: 0, paddingLeft: '16px' }}>{children}</ul>,
                      ol: ({ children }) => <ol style={{ margin: 0, paddingLeft: '16px' }}>{children}</ol>,
                      li: ({ children }) => <li style={{ marginBottom: '2px' }}>{children}</li>,
                    }}
                  >
                    {labelText}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  )
}

export default memo(CustomEdge)
